/**
 * The API client.
 *
 * It carries three rules from the server's side of the boundary, because a
 * client that gets them wrong undoes the work done there:
 *
 *   1. `X-AC2-Request: 1` on every mutation. The session cookie is
 *      SameSite=Strict so a cross-site request cannot carry it at all; this is
 *      the second lock and the server refuses a mutation without it.
 *
 *   2. **404 on a read means "nothing here for you", not "the request broke".**
 *      The server answers 404 rather than 403 for an unpermitted read, on
 *      purpose: 403 would distinguish "exists but not yours" from "does not
 *      exist". So the UI must render absence, not an error — and the two are
 *      different screens. `Absent` exists for exactly that.
 *
 *   3. A refusal is deliberately flat and always carries a correlation id. The
 *      id is the only thread back to what actually happened, so it is surfaced
 *      rather than swallowed.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** The caller may not see this, or it does not exist. The UI must not say which. */
export class Absent extends Error {
  readonly status = 404;
  constructor(readonly correlationId?: string) {
    super("Nothing here.");
    this.name = "Absent";
  }
}

/** No live session. The shell sends the person to sign in. */
export class Unauthenticated extends Error {
  readonly status = 401;
  constructor() {
    super("Not signed in.");
    this.name = "Unauthenticated";
  }
}

interface ErrorBody {
  error?: string;
  correlationId?: string;
}

async function throwResponse(res: Response, method: string): Promise<never> {
  const raw = await res.text();
  let detail: ErrorBody = {};
  if (raw.length > 0) {
    try {
      detail = JSON.parse(raw) as ErrorBody;
    } catch {
      detail = {};
    }
  }

  if (res.status === 401) throw new Unauthenticated();
  if (res.status === 404 && method === "GET") {
    throw new Absent(detail.correlationId);
  }
  throw new ApiError(
    res.status,
    detail.error ?? `Request failed (${res.status}).`,
    detail.correlationId,
  );
}

/**
 * Noticing that the platform has been redeployed under you.
 *
 * A single-page application holds the JavaScript it loaded until the
 * document is reloaded. Shipping a fix does nothing for a tab that is
 * already open, and the person in that tab has no way to know: they see
 * the old behaviour, report it as unfixed, and get told to hard-refresh.
 *
 * So the server stamps every response with the build it is serving
 * (`x-ac2-build`, derived from the hashed asset name in index.html, which
 * changes exactly when the frontend does). The first one seen is the
 * build this tab is running. Any later response that disagrees means a
 * deploy happened, and the shell can say so.
 *
 * Deliberately a notice and not an automatic reload: reloading out from
 * under someone mid-form throws away what they were typing, and this
 * platform asks people to type things that are a nuisance to retype.
 */
let runningBuild: string | null = null;
const buildWatchers = new Set<() => void>();
let staleBuild = false;

export function onBuildChanged(fn: () => void): () => void {
  buildWatchers.add(fn);
  return () => buildWatchers.delete(fn);
}

export function buildIsStale(): boolean {
  return staleBuild;
}

function noteBuild(res: Response): void {
  const seen = res.headers.get("x-ac2-build");
  // "dev" means the server is running without a built frontend, which is
  // every local session. Comparing it would cry wolf on every reload.
  if (!seen || seen === "dev") return;

  if (runningBuild === null) {
    runningBuild = seen;
    return;
  }
  if (seen !== runningBuild && !staleBuild) {
    staleBuild = true;
    for (const fn of buildWatchers) fn();
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (method !== "GET") headers["X-AC2-Request"] = "1";
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(path, {
    method,
    headers,
    // Same-origin, so the Strict cookie travels. Stated rather than defaulted.
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  noteBuild(res);

  if (res.status === 204) return undefined as T;

  if (!res.ok) return throwResponse(res, method);
  return (await res.json()) as T;
}

async function uploadFile<T>(path: string, file: File): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: {
      "X-AC2-Request": "1",
      "Content-Type": file.type || "application/octet-stream",
    },
    credentials: "same-origin",
    body: file,
  });
  if (!res.ok) return throwResponse(res, "POST");
  return (await res.json()) as T;
}

export interface DownloadedFile {
  blob: Blob;
  name: string;
}

async function downloadFile(path: string): Promise<DownloadedFile> {
  const res = await fetch(path, { method: "GET", credentials: "same-origin" });
  if (!res.ok) return throwResponse(res, "GET");

  const disposition = res.headers.get("content-disposition") ?? "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const fallback = disposition.match(/filename="([^"]+)"/i)?.[1];
  let name = fallback ?? "download";
  if (encoded) {
    try {
      name = decodeURIComponent(encoded);
    } catch {
      // The safe ASCII fallback still names the download.
    }
  }
  return { blob: await res.blob(), name };
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  /** Replace, as distinct from amend. See PUT /api/me/profile. */
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
  uploadFile,
  downloadFile,
};

// --------------------------------------------------------------- shapes ----
// Mirrors of what the domain layer actually returns — read from
// src/domain/*.ts rather than assumed. Dates arrive as JSON strings even where
// the server types them as Date, so they are strings here; `format.ts` is the
// only place that parses them.

export type Role =
  | "super_admin"
  | "principal_partner"
  | "project_lead"
  | "consultant"
  | "client";

export type Scope = "task" | "workstream" | "engagement";

export interface Me {
  userId: string;
  fullName: string;
  role: Role;
}

export interface EngagementHeader {
  id: string;
  reference: string;
  name: string;
  status: string;
  organisation: string;
  startedOn: string | null;
  targetCompletion: string | null;
}

export interface CommandView {
  engagement: {
    id: string;
    reference: string;
    name: string;
    status: string;
    organisation: string;
    startedOn: string | null;
    targetCompletion: string | null;
  };
  progress: { calculated: number; official: number };
  milestones: Array<{
    id: string;
    name: string;
    dueOn: string | null;
    completedAt: string | null;
    openTasks: number;
    overdue: boolean;
  }>;
  needsAttention: {
    overdueTasks: Array<{
      id: string;
      title: string;
      dueOn: string;
      assignee: string | null;
    }>;
    blockedTasks: Array<{ id: string; title: string }>;
    unassignedTasks: Array<{ id: string; title: string }>;
  };
  awaitingDecision: Array<{
    deliverableId: string;
    title: string;
    versionId: string;
    versionNo: number;
    submittedBy: string;
    submittedAt: string;
  }>;
  openRevisions: Array<{
    id: string;
    deliverableTitle: string;
    requestedBy: string;
    body: string;
  }>;
  recentActivity: Array<{
    action: string;
    objectType: string;
    at: string;
    actor: string | null;
  }>;
}

export interface MyWork {
  today: Array<{
    taskId: string;
    title: string;
    /** What the task involves. The column existed; nothing ever read it. */
    body: string | null;
    engagement: string;
    engagementId: string;
    dueOn: string | null;
    overdue: boolean;
    status: string;
  }>;
  revisionsRequested: Array<{
    deliverableId: string;
    /** Where the deliverable lives, so "Make changes" can go somewhere. */
    engagementId: string;
    title: string;
    versionNo: number;
    body: string;
    requestedBy: string;
  }>;
  context: Array<{
    engagementId: string;
    engagement: string;
    scope: Scope;
    expiresAt: string | null;
    lead: string | null;
  }>;
}

export interface AssignmentOffer {
  id: string;
  engagementId: string;
  engagement: string;
  scope: Scope;
  brief: string | null;
  startsAt: string;
  expiresAt: string | null;
}

export interface ClientPortal {
  snapshot: {
    engagement: string;
    reference: string;
    status: string;
    progress: number;
    leadPartner: string | null;
    nextMilestone: { name: string; dueOn: string | null } | null;
    targetCompletion: string | null;
  };
  actionCentre: {
    openActions: Array<{
      id: string;
      title: string;
      kind: string;
      dueOn: string | null;
      overdue: boolean;
    }>;
    awaitingYourApproval: Array<{
      deliverableId: string;
      title: string;
      versionId: string;
      versionNo: number;
    }>;
    /**
     * What is no longer open — `responded` or `closed` — newest first, capped
     * at 50 by the server. `respondedAt` is null for an action the firm closed
     * without the client answering, so the Actions page says "Closed by your
     * team" rather than inventing a date.
     */
    pastActions: Array<{
      id: string;
      title: string;
      kind: string;
      dueOn: string | null;
      respondedAt: string | null;
    }>;
  };
  journey: {
    completed: Array<{ name: string; completedAt: string }>;
    active: Array<{ name: string; dueOn: string | null }>;
    upcoming: Array<{ name: string; dueOn: string | null }>;
  };
  documents: Array<{
    id: string;
    name: string;
    folder: string;
    /**
     * What a download actually needs.
     *
     * Downloads are addressed by VERSION. The portal listed documents by id
     * with a Download button beside each, so the button had nothing to call
     * even once it had a handler. Null only when a document has no version
     * at all, which cannot be downloaded and must not offer to be.
     */
    latestVersionId: string | null;
  }>;
  approvedDeliverables: Array<{
    id: string;
    title: string;
    versionId: string;
    versionNo: number;
    /** Null where the firm approved it without asking the client. */
    approvedAt: string | null;
  }>;
}

export type FolderKey =
  | "01_client_documents"
  | "02_research"
  | "03_working_files"
  | "04_draft_deliverables"
  | "05_final_deliverables"
  | "06_commercial"
  | "07_communications"
  | "08_meetings_transcripts";

export interface FolderListing {
  folderKey: FolderKey;
  driveFolderId: string | null;
  documents: Array<{
    id: string;
    name: string;
    classification: string;
    versionCount: number;
    latestVersionId: string | null;
    uploadedBy: string;
  }>;
}

export interface AccessRecord {
  userId: string;
  fullName: string;
  objectType: string;
  objectId: string;
  at: string;
  purpose: string | null;
  /** How it was read. A preview is an access and is logged as one. */
  action: "download" | "preview";
}

// --------------------------------------------------- knowledge + intake ----

export type Classification =
  | "client_visible"
  | "internal"
  | "restricted"
  | "highly_restricted";

export type SuggestionKind =
  | "decision"
  | "commitment"
  | "date"
  | "risk"
  | "action";

export interface Suggestion {
  kind: SuggestionKind;
  /** Kept verbatim so a reviewer can judge the proposal in context. */
  quote: string;
  summary: string;
  on?: string;
  confidence: "low" | "medium";
}

export type InboxStatus = "pending" | "approved" | "rejected";

export interface InboxItem {
  id: string;
  engagementId: string;
  /** Null when the source exists but is outside the reader's visibility. */
  sourceId: string | null;
  sourceKind: string | null;
  extracted: unknown;
  suggestedDestination: string | null;
  status: InboxStatus;
  classification: Classification;
  createdBy: string;
  createdByName: string;
  reviewedBy: string | null;
  /** Null means unreviewed; a hidden reviewer is returned as a fixed label. */
  reviewedByName: string | null;
  reviewedAt: string | null;
}

export interface MemoryEntry {
  id: string;
  title: string;
  classification: Classification;
  /** The latest revision. Earlier wording remains in history. */
  body: string | null;
  revisions: number;
  lastAmendedAt: string | null;
  sourceKind: string | null;
}

export interface MemoryRevision {
  id: string;
  body: string;
  authorId: string;
  authorName: string;
  createdAt: string;
}

export type IngestionKind =
  | "whatsapp"
  | "telegram"
  | "otter"
  | "email"
  | "manual"
  | "call"
  | "meeting"
  | "site_visit"
  | "letter"
  | "sms"
  | "document";

export interface SourceRecord {
  id: string;
  kind: IngestionKind;
  submittedBy: string;
  submittedByName: string;
  submittedAt: string;
  /** When it was said, when known; not when it was filed. */
  receivedAt: string | null;
  externalRef: string | null;
  classification: Classification;
  /** What it was about, in a person's words. Null before migration 034. */
  subject: string | null;
  workstreamId: string | null;
  workstream: string | null;
  /** What was actually said. Null only on rows filed before it was returned. */
  rawText: string | null;
  proposals: number;
}

export interface IngestResult {
  sourceId: string;
  proposed: string[];
  suggestions: Suggestion[];
}

// ------------------------------------------------------------ invitations ----

/** An invitation that has not been redeemed yet. Never carries the token. */
export interface PendingInvitation {
  id: string;
  email: string;
  fullName: string;
  role: Role;
  createdAt: string;
  expiresAt: string;
  /** Revocation is expiry, so a revoked invitation reads as expired here. */
  expired: boolean;
}

/**
 * The response to issuing one. `token` is returned exactly once and is not
 * recoverable afterwards — only its SHA-256 reaches the database — so the
 * screen that receives this must show the link before it navigates away.
 */
export interface IssuedInvitation {
  id: string;
  token: string;
  expiresAt: string;
}

/** What the invited person is shown, once, after redeeming. */
export interface RedeemedAccount {
  userId: string;
  email: string;
  fullName: string;
  role: Role;
  password: string;
  mfaSecret: string;
  otpauth: string;
}

// ------------------------------------------------- engagements and people ----

/** One engagement in the index. Filtered by row-level security before it ships. */
export interface EngagementSummary {
  id: string;
  reference: string;
  name: string;
  status: string;
  classification: Classification;
  organisation: string;
  startedOn: string | null;
  targetCompletion: string | null;
  portalReadyAt: string | null;
  portalActivatedAt: string | null;
  /** Prose: what the work is. Null until somebody writes it. */
  brief: string | null;
  /**
   * Whether you may shape this engagement. Sent by the server, computed by
   * `app.may_manage` — role AND reach, which the session alone cannot tell
   * you. Use this to decide what to render; the server decides what to
   * allow, and the two now agree.
   */
  mayManage: boolean;
}

export interface OrganisationSummary {
  id: string;
  name: string;
  sector: string | null;
  country: string | null;
}

/**
 * Everyone the caller may see. Includes clients — the Lead picker filters
 * them out rather than the endpoint, because People must be able to show
 * somebody who has accepted an invitation.
 */
export interface Person {
  id: string;
  fullName: string;
  email: string;
  role: Role;
  isSuspended: boolean;
  mfaEnrolled: boolean;
  createdAt: string;
}

/** One person on an engagement, in whatever capacity they are there. */
/**
 * Who can see the client portal, and what is published to them.
 *
 * Counts live publications only: a revoked publication is no longer
 * visible to anybody, and counting it would overstate what the client
 * actually has.
 */
export interface PortalAudience {
  people: Array<{
    userId: string;
    fullName: string;
    email: string;
    lastSignedInAt: string | null;
  }>;
  published: { deliverables: number; documents: number };
}

export interface TeamMember {
  kind: "member" | "consultant" | "client";
  userId: string;
  fullName: string;
  email: string;
  role: Role;
  /** Assignments only: the row to revoke or extend. */
  assignmentId: string | null;
  scope: string | null;
  status: string;
  startsAt: string | null;
  expiresAt: string | null;
  /**
   * Assignments only: when somebody confirmed the handover, or null.
   *
   * Not a security fact. Access lapsed at `expiresAt` or at revocation,
   * whichever came first. This says whether a person checked the work came
   * back, which is a different question and the one nobody was asked.
   */
  offboardedAt: string | null;
}

// ------------------------------------------------------------------ firm ----

/** Counts for the firm overview. Super Admin only; the server enforces it. */
export interface FirmOverview {
  clients: { total: number; active: number };
  engagements: Record<string, number>;
  engagementsTotal: number;
  people: {
    byRole: Record<string, number>;
    suspended: number;
    consultantsEngaged: number;
    invitationsPending: number;
  };
  work: {
    tasksOpen: number;
    tasksOverdue: number;
    deliverablesAwaitingApproval: number;
    deliverablesInRevision: number;
    clientActionsOpen: number;
  };
  portals: { activated: number; readyNotActivated: number };
  documents: { total: number; storedBytes: number };
  asOf: string;
}

export type AttentionKind =
  | "task_overdue"
  | "awaiting_approval"
  | "revision_requested"
  | "client_action_open"
  | "assignment_expiring"
  // 043. A register meant to show the most embarrassing thing in the firm
  // could not mention an unpaid bill until now.
  | "invoice_overdue";

/** One thing needing a person, anywhere in the firm. Always names its engagement. */
export interface AttentionItem {
  kind: AttentionKind;
  engagementId: string;
  engagement: string;
  reference: string;
  subject: string;
  dueOn: string | null;
  daysLate: number;
}

/** Workstreams and milestones — what an engagement is made of. */
/**
 * The board: workstreams with their tasks.
 *
 * `mayManage` and each task's `mayMove` come from the server, not from the
 * session role — the same rule as `EngagementSummary.mayManage`. A
 * consultant may move the task assigned to them and nothing else, which no
 * amount of role-reading in the browser could work out.
 */
export interface WorkTask {
  id: string;
  title: string;
  body: string | null;
  status: string;
  dueOn: string | null;
  overdue: boolean;
  assigneeId: string | null;
  assignee: string | null;
  milestoneId: string | null;
  milestone: string | null;
  /** May change its status. Manager, or the person it is assigned to. */
  mayMove: boolean;
  /** May rewrite it — name, owner, date. Manager only; 031 enforces it. */
  mayEdit: boolean;
}

/**
 * Money, everywhere in these types, is a whole number of **minor units** —
 * kobo, cents. Never a float, never a decimal string. `money()` in
 * format.ts is the only place that divides by 100, and it does it for
 * display alone.
 */
export interface InvoiceLine {
  id: string;
  description: string;
  /** Hundredths. 750 is 7.5 units. */
  quantity: number;
  unitAmount: number;
  amount: number;
  sequence: number;
}

export interface InvoicePayment {
  id: string;
  amount: number;
  paidOn: string;
  reference: string | null;
  note: string | null;
  recordedBy: string | null;
  recordedAt: string;
}

export interface Invoice {
  id: string;
  reference: string;
  status: string;
  currency: string;
  issuedOn: string | null;
  dueOn: string | null;
  notes: string | null;
  sentAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  createdBy: string | null;
  createdAt: string;
  lines: InvoiceLine[];
  payments: InvoicePayment[];
  total: number;
  paid: number;
  outstanding: number;
  overdue: boolean;
  mayBill: boolean;
}

export interface BillsView {
  mayBill: boolean;
  currency: string;
  invoices: Invoice[];
  /** Across sent invoices only. A draft is nobody's debt. */
  totals: { billed: number; paid: number; outstanding: number; overdue: number };
}

export type RaidKind = "risk" | "assumption" | "issue" | "dependency";
export type RaidGrade = "low" | "medium" | "high";

/**
 * One entry in the RAID log.
 *
 * The four kinds are genuinely different questions, which is why the
 * optional fields are optional: likelihood and impact belong to a risk
 * alone, `owedBy` to a dependency alone, and migration 032 has CHECKs that
 * refuse the other combinations rather than trusting the interface.
 */
export interface RaidEntry {
  id: string;
  kind: RaidKind;
  title: string;
  detail: string | null;
  workstreamId: string | null;
  workstream: string | null;
  ownerUserId: string | null;
  owner: string | null;
  likelihood: RaidGrade | null;
  impact: RaidGrade | null;
  owedBy: string | null;
  dueOn: string | null;
  overdue: boolean;
  status: string;
  classification: string;
  raisedBy: string | null;
  raisedAt: string;
  closedAt: string | null;
  closedNote: string | null;
  /** The issue this risk became, if it materialised. */
  becameId: string | null;
  /** Whether you may curate it: manage the engagement, or own the entry. */
  mayCurate: boolean;
}

/** Something this workstream is meant to produce. */
export interface StreamOutput {
  id: string;
  title: string;
  status: string;
  classification: string;
  /** The newest submitted version — what you send or ask changes on. */
  latestVersionId: string | null;
  latestVersionNo: number | null;
  submittedBy: string | null;
  submittedAt: string | null;
  /**
   * The approved version, which is not the same thing.
   * `deliverable.current_version_id` is set only by the approval trigger,
   * and the client portal joins on it. Showing the latest under this name
   * would tell a Lead a draft had been approved.
   */
  approvedVersionNo: number | null;
  versions: number;
  /** With the client for sign-off, not yet answered. */
  awaitingClient: boolean;
}

export interface DeliverableVersionRecord {
  id: string;
  versionNo: number;
  driveFileId: string;
  submittedBy: string;
  submittedByName: string | null;
  submittedAt: string;
}

export type Health = "on_track" | "at_risk" | "off_track";

export interface WorkStream {
  id: string;
  name: string;
  sequence: number;
  /** The question this stream exists to answer. */
  intent: string | null;
  leadUserId: string | null;
  lead: string | null;
  health: string;
  healthNote: string | null;
  nextMilestone: { id: string; name: string; dueOn: string | null } | null;
  outputs: StreamOutput[];
  tasks: WorkTask[];
}

export interface WorkView {
  mayManage: boolean;
  workstreams: WorkStream[];
  /** Tasks in no workstream. Shown, not hidden — unfiled work is work. */
  unfiled: WorkTask[];
}

export interface EngagementParts {
  /**
   * The highest classification you may write on this engagement.
   * Consultant: internal. Lead: restricted. Partner: restricted, or the
   * top if named for sealed material here. Super Admin: the top.
   *
   * Use it to build the classification options. Offering more than this
   * produces a bare "You do not have permission to do that" from the
   * database with nothing to explain which field was the problem.
   */
  ceiling: string;
  workstreams: { id: string; name: string; sequence: number }[];
  milestones: {
    id: string;
    name: string;
    workstreamId: string | null;
    dueOn: string | null;
    completedAt: string | null;
  }[];
}

// ---------------------------------------------------------- overrides ----

/**
 * One grant or one deny, live or withdrawn.
 *
 * The same shape for both, deliberately. They are opposite instruments —
 * rule 7 lifts one named thing above a ceiling, rule 4 beats every other
 * rule — but the record of each answers the same four questions: who, what,
 * why, and who took it back. A register that laid them out differently
 * would make them harder to read side by side, which is the only way to
 * read them.
 *
 * Names rather than ids, resolved by the server: three uuids and an object
 * id make a log file, not a record.
 */
export interface OverrideRecord {
  id: string;
  userId: string;
  userName: string | null;
  objectType: string;
  objectId: string | null;
  /** The thing's own name where its type has one. */
  objectName: string | null;
  reason: string;
  issuedBy: string;
  issuedByName: string | null;
  issuedAt: string;
  /** Grants only. A deny does not expire; it is lifted or it stands. */
  expiresAt: string | null;
  withdrawnAt: string | null;
  withdrawnBy: string | null;
  withdrawnByName: string | null;
  withdrawalReason: string | null;
  live: boolean;
  /** Grants only: the ceiling this one lifts the person to. */
  maxClassification: Classification | null;
}

export interface OverrideRegister {
  grants: OverrideRecord[];
  denies: OverrideRecord[];
}

// -------------------------------------------------------------- bench ----

/**
 * One consultant on the bench, with their entry where they have written one.
 *
 * Somebody with `hasEntry: false` still appears — they are a consultant the
 * firm can staff and the most useful row on the screen, because they are the
 * gap. The read behind this inner-joined the profile until migration 045,
 * so those people were invisible and the bench opened empty on a new firm.
 */
export interface BenchEntry {
  userId: string;
  fullName: string;
  email: string;
  /**
   * Their role. Since 051 the bench is the whole firm ranked, not a list of
   * consultants, and what comes back depends on where the caller sits
   * relative to each person.
   */
  role: Role;
  headline: string | null;
  expertise: string[] | null;
  bio: string | null;
  available: string | null;
  hasEntry: boolean;
  /** Notes standing against them. Always 0 on the caller's own row. */
  notes: number;
}

/**
 * What the firm has written about somebody it staffs.
 *
 * Append-only: there is no edit and no delete, by construction in 051. Never
 * returned to the person it is about — `note_read` requires the subject not be
 * the caller, so a consultant asking about themselves gets an empty list.
 */
export interface BenchNote {
  id: string;
  body: string;
  authorId: string;
  author: string | null;
  createdAt: string;
}

export interface MyProfile {
  headline: string | null;
  expertise: string[] | null;
  bio: string | null;
  availability: string | null;
}

/**
 * One line of a consultant's history.
 *
 * `rating` and `timeliness` come from `consultant_evaluation`, which is
 * Restricted — so they arrive **null** for the consultant reading their own
 * history and filled in for a Partner reading the same rows. Nothing in the
 * interface decides that and nothing in the interface should: render what
 * arrived.
 */
export interface ConsultantHistoryRow {
  engagementId: string;
  engagement: string;
  scope: string;
  status: string;
  started: string | null;
  ended: string | null;
  rating: number | null;
  timeliness: number | null;
}

// ------------------------------------------------------------ release ----

/** One thing that can be handed to the client, in whichever state it is in. */
export interface ReleasableItem {
  type: string;
  id: string;
  name: string;
  classification: string;
  /** Live publication only. A revoked one is not a publication. */
  publishedAt: string | null;
  publishedByName: string | null;
  revokedAt: string | null;
  revokedByName: string | null;
  /**
   * Published AND at or below the client's ceiling.
   *
   * Both, or neither. A published item that was later reclassified upward
   * is still released and no longer visible, and this is the only field in
   * the product that can tell you so.
   */
  visible: boolean;
}

export interface ReleaseBoard {
  /** Whether this caller may publish and withdraw, not merely look. */
  mayRelease: boolean;
  /** Whether the portal is open at all. Releasing into a shut one is silent. */
  portalOpen: boolean;
  /** The engagement itself is above what a client may see. */
  engagementAboveClient: boolean;
  held: ReleasableItem[];
  released: ReleasableItem[];
}

// --------------------------------------------------------------- firm ----
//
// The four reads added in 043. Super Admin only — the SECURITY DEFINER
// functions behind them each re-check, so a Partner calling these gets a
// refusal rather than a thinner answer.

/**
 * What has been billed in one currency, and what is still owed in it.
 *
 * **One entry per currency, and never a total.** `033_bills.sql`: a firm
 * billing in more than one currency must not add them up. There is
 * deliberately no `total` field and no helper that produces one — naira
 * plus dollars is not a number.
 *
 * Minor units throughout, as everywhere else. Use `money()` to render.
 */
export interface MoneyByCurrency {
  currency: string;
  billed: number;
  collected: number;
  outstanding: number;
  overdue: number;
  invoices: number;
  overdueInvoices: number;
}

/** One consultant, and whether the firm can put them on something. */
export interface CapacityRow {
  userId: string;
  fullName: string;
  engagements: number;
  earliestEnd: string | null;
  /** "freeing" is within 30 days of their first assignment ending. */
  state: "engaged" | "freeing" | "free";
}

/**
 * One client organisation per currency billed.
 *
 * A client billed in two currencies appears twice. A client never billed
 * appears once with a null currency — they are still a client, and
 * dropping them would turn the list into a list of debtors.
 */
export interface ClientRow {
  organisationId: string;
  organisation: string;
  engagements: number;
  live: number;
  currency: string | null;
  billed: number;
  outstanding: number;
  lastActivity: string | null;
}

/** Live work, soonest to end first. */
export interface PipelineRow {
  engagementId: string;
  engagement: string;
  reference: string;
  organisation: string;
  status: string;
  targetCompletion: string | null;
  /** Negative when the target has passed; null when there is no date. */
  daysLeft: number | null;
  billed: number;
  currency: string | null;
}

// ------------------------------------------------------------- Catch (050) --
//
// The phase before a client is a client. A prospect is a provisional client;
// it becomes an `organisation` — minting its permanent filing code — at the
// moment an engagement is opened for it. Additive: nothing above changes.

export type ProspectStatus = "open" | "converted" | "declined";
export type IntroCallStatus = "draft" | "complete";

export interface Prospect {
  id: string;
  name: string;
  sector: string | null;
  country: string | null;
  source: string | null;
  ownerUserId: string;
  owner: string | null;
  status: ProspectStatus;
  convertedOrganisationId: string | null;
  convertedAt: string | null;
  declinedAt: string | null;
  declinedReason: string | null;
  createdAt: string;
  /** How many calls have been held, and when the most recent one was. */
  calls: number;
  /** How many are complete. Drives "ready for an engagement" on the list. */
  completedCalls: number;
  lastCalledOn: string | null;
  /** From the most recent call that named one, so the list can be worked from. */
  nextStep: string | null;
  nextStepBy: string | null;
}

export interface ProspectContact {
  id: string;
  fullName: string;
  roleTitle: string | null;
  email: string | null;
  phone: string | null;
  isDecisionMaker: boolean;
}

/**
 * One introductory call, six stages.
 *
 * Every capture is nullable because a call is filled in as it runs. The four
 * that completion requires — `problemInTheirWords`, `mustBeTrue`,
 * `decisionProcess`, `nextStep` — are enforced by the database, not by this
 * type; see `REQUIRED_TO_COMPLETE` in src/domain/catch.ts.
 */
export interface IntroCall {
  id: string;
  prospectId: string;
  calledOn: string;
  ranBy: string;
  ranByName: string | null;
  theirAttendees: string | null;

  purposeAgreed: string | null;
  howTheyFoundUs: string | null;

  promptedNow: string | null;
  problemInTheirWords: string | null;
  whoFeelsIt: string | null;
  alreadyTried: string | null;

  mustBeTrue: string | null;
  successMeasures: string | null;

  targetDate: string | null;
  dateMattersBecause: string | null;
  budgetUndecided: boolean;
  /** Minor units — kobo, not naira. Divide by 100 to display. */
  budgetLow: number | null;
  budgetHigh: number | null;
  budgetCurrency: string | null;
  dependsOn: string | null;

  whoSigns: string | null;
  decisionProcess: string | null;
  othersInTheRoom: string | null;
  alsoConsidering: string | null;

  recap: string | null;
  openQuestions: string | null;
  nextStep: string | null;
  nextStepOwnerUserId: string | null;
  nextStepOwner: string | null;
  nextStepBy: string | null;

  status: IntroCallStatus;
  completedAt: string | null;
}

export interface ProspectDetail {
  prospect: Prospect;
  contacts: ProspectContact[];
  calls: IntroCall[];
}

// ------------------------------------------------------- preview gateway ----
//
// 052's six firm-side routes. The client has no route here at all and needs
// none: their door is the emailed link, which the gateway on
// `<client>-preview.arikco.com` exchanges for a cookie. Nothing a client can
// call appears below, and `origin_url` deliberately never reaches them — an
// origin they know is one they can visit directly, for ever, with nothing we
// could withdraw.

/** One preview on an engagement. Retired ones are returned too. */
export interface Preview {
  id: string;
  engagementId: string;
  /** `<client>-preview.arikco.com`. One label — see src/http/preview.ts. */
  host: string;
  /** Where the build actually lives. Firm-side only. */
  originUrl: string;
  label: string;
  createdAt: string;
  retiredAt: string | null;
}

/** Who can currently open a build. Never the tokens. */
export interface PreviewPass {
  id: string;
  userId: string;
  fullName: string;
  email: string;
  issuedAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  uses: number;
  revokedAt: string | null;
  /** Neither revoked nor expired. What the screen should actually show. */
  live: boolean;
}

/** The link, returned once. We keep only its hash. */
export interface IssuedPass {
  id: string;
  link: string | null;
  expiresAt: string;
  emailed: boolean;
}

export const previews = {
  list: (engagementId: string) =>
    api.get<Preview[]>(`/api/engagements/${engagementId}/previews`),
  create: (
    engagementId: string,
    body: { host: string; originUrl: string; label: string },
  ) => api.post<{ id: string }>(`/api/engagements/${engagementId}/previews`, body),
  retire: (previewId: string) =>
    api.post<void>(`/api/previews/${previewId}/retire`),
  passes: (previewId: string) =>
    api.get<PreviewPass[]>(`/api/previews/${previewId}/passes`),
  issue: (previewId: string, userId: string) =>
    api.post<IssuedPass>(`/api/previews/${previewId}/passes`, { userId }),
  revoke: (passId: string) =>
    api.post<void>(`/api/preview-passes/${passId}/revoke`),
};
